import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Eye, Send, Loader2 } from 'lucide-react';
import { PostCard } from '@/components/legacy/LegacyChannel';

const API_URL = window.location.origin;

export const WitnessWall = ({ user }) => {
  const [posts, setPosts] = useState([]);
  const [starters, setStarters] = useState([]);
  const [members, setMembers] = useState([]);
  const [subjectId, setSubjectId] = useState('');
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'mine' | member id

  const fetchWall = async () => {
    try {
      const params = filter === 'all' ? '' : `?subject_id=${filter === 'mine' ? user?.id : filter}`;
      const res = await axios.get(`${API_URL}/api/legacy/witness${params}`);
      setPosts(res.data.posts);
      setStarters(res.data.starters || []);
    } catch (error) {
      console.error('Failed to load witness wall:', error);
    }
  };

  useEffect(() => {
    fetchWall();
    axios.get(`${API_URL}/api/legacy/members`).then(res => setMembers(res.data)).catch(() => {});
  }, [filter]);

  const handlePost = async () => {
    if (!subjectId) { toast.error('Choose the woman you are witnessing'); return; }
    if (!content.trim()) { toast.error('Share what you see in her'); return; }
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/legacy/witness`, { subject_id: subjectId, content });
      toast.success('She has been witnessed 🤍');
      setContent('');
      setSubjectId('');
      fetchWall();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const otherMembers = members.filter(m => m.id !== user?.id);

  return (
    <div className="space-y-6" data-testid="witness-wall">
      <Card className="card-base border-terracotta/20 bg-gradient-to-br from-terracotta/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="h-5 w-5 text-terracotta" />
            <p className="text-xs uppercase tracking-widest text-terracotta">Witness Wall</p>
          </div>
          <p className="font-heading text-xl text-forest">Reflect the gifts, courage, growth, and impact you see in one another.</p>
        </CardContent>
      </Card>

      {/* Composer */}
      <Card className="card-base">
        <CardContent className="p-4 space-y-3">
          <Select value={subjectId} onValueChange={setSubjectId}>
            <SelectTrigger data-testid="witness-subject-select">
              <SelectValue placeholder="I am witnessing..." />
            </SelectTrigger>
            <SelectContent>
              {otherMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-1">
            {starters.map(s => (
              <button key={s} onClick={() => setContent(c => (c ? `${c.trimEnd()}\n${s} ` : s + ' '))}
                className="text-xs px-2 py-1 rounded-full bg-sand/70 text-gray-600 hover:bg-sand">
                {s}
              </button>
            ))}
          </div>
          <Textarea value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="I see in you..." rows={3} data-testid="witness-content-input" />
          <div className="flex justify-end">
            <Button onClick={handlePost} disabled={posting} className="bg-forest hover:bg-forest/90 text-white" data-testid="witness-submit-btn">
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Witness Her
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setFilter('all')} className={`text-xs px-3 py-1.5 rounded-full border ${filter === 'all' ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`} data-testid="witness-filter-all">
          The Wall
        </button>
        <button onClick={() => setFilter('mine')} className={`text-xs px-3 py-1.5 rounded-full border ${filter === 'mine' ? 'bg-forest text-white border-forest' : 'border-sand text-gray-500'}`} data-testid="witness-filter-mine">
          My Witness Archive
        </button>
        {filter !== 'all' && filter !== 'mine' && (
          <button onClick={() => setFilter('all')} className="text-xs px-3 py-1.5 rounded-full bg-terracotta text-white flex items-center gap-1" data-testid="witness-filter-member">
            Witnessing {members.find(m => m.id === filter)?.full_name || 'a sister'} · ✕
          </button>
        )}
      </div>
      {filter === 'mine' && (
        <p className="text-sm text-gray-500 italic">Everything the circle sees in you, kept in one place. Return here whenever you need to remember.</p>
      )}

      {/* Feed */}
      {posts.length > 0 ? (
        <div className="space-y-4">
          {posts.map(post => (
            <div key={post.id}>
              <p className="text-xs text-gray-400 mb-1 ml-1">
                Witnessing <button onClick={() => setFilter(post.subject_id)} className="font-medium text-terracotta hover:underline">{post.subject_name}</button>
              </p>
              <PostCard post={post} user={user} channel="witness" onRefresh={fetchWall} />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-6">
          {filter === 'mine' ? 'Your archive will fill as your sisters witness you.' : 'Be the first to witness a sister.'}
        </p>
      )}
    </div>
  );
};

export default WitnessWall;
